// frontend/src/screens/admin/WardPreShiftListScreen.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { lightGreen, red, orange, blue, grey } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';

import {
  useGetPreShiftsQuery,
  useApprovePreShiftMutation,
  useRejectPreShiftMutation,
  useDeletePreShiftMutation,
} from '../../slices/preShiftApiSlice';

import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.pending-row {
    background-color: ${orange[50]};
  }
  & .${gridClasses.row}.approved-row {
    background-color: ${lightGreen[100]};
  }
  & .${gridClasses.row}.rejected-row {
    background-color: ${red[100]};
  }
`;

// Scrollable container styles
const scrollableTableStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};

const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// ─── Status → colour / icon lookup, shared by the grid row classes (desktop)
// and the card accent colour / chip (mobile) ────────────────────────────────
const STATUS_META = {
  Pending:  { chipColor: 'warning', icon: <HistoryIcon fontSize="small" />,      accent: orange[400],     rowClass: 'pending-row' },
  Approved: { chipColor: 'success', icon: <CheckCircleIcon fontSize="small" />,  accent: lightGreen[500], rowClass: 'approved-row' },
  Rejected: { chipColor: 'error',   icon: <CancelIcon fontSize="small" />,       accent: red[400],        rowClass: 'rejected-row' },
};
const getStatusMeta = (status) => STATUS_META[status] || { chipColor: 'default', icon: null, accent: grey[400], rowClass: '' };

// Helper function to extract MongoDB OID
const extractMongoId = (id) => {
  if (!id) return null;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id._id) return extractMongoId(id._id);
  return id?.toString() || null;
};

const WardPreShiftListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Manager scope info (display only — actual filtering now happens
  // server-side in getPreShifts via getManagerAllowedWardNames/applyWardScope,
  // so the list this screen receives is already restricted to wards the
  // manager can see). Kept here purely to label the banner/subheader/empty
  // states correctly for both directorate-scoped and onlyOwnWard managers. ──
  const managerDirectorate = userInfo?.directorateName || '';
  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true;
  const scopeLabel = managerOnlyOwnWard
    ? `Own Ward: ${userInfo?.speciality || 'not set'}`
    : (managerDirectorate || 'your assigned wards');

  const [filters, setFilters] = useState({
    wardDepartmentArea: '',
    categoryOfStaff: '',
    preShiftStatus: '',
    dateRange: {
      start: '',
      end: '',
    },
  });

  // State for modals
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPreShift, setSelectedPreShift] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showFilters, setShowFilters] = useState(true);
 const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });


  // API hooks — getPreShifts is now ward-scoped server-side for managers,
  // so this returns only pre-shifts within the caller's allowed wards.
  const { data: preShifts, isLoading, isError, error, refetch } = useGetPreShiftsQuery();
  const { data: hospitalLocations, isLoading: loadingLocations } = useGetHospitalLocationsQuery();

  const [approvePreShift, { isLoading: approving }] = useApprovePreShiftMutation();
  const [rejectPreShift, { isLoading: rejecting }] = useRejectPreShiftMutation();
  const [deletePreShift, { isLoading: deleting }] = useDeletePreShiftMutation();

  // Create a map of hospital location IDs to their data
  const hospitalLocationMap = useMemo(() => {
    if (!hospitalLocations) return {};
    const map = {};
    hospitalLocations.forEach(location => {
      const locationId = extractMongoId(location._id);
      if (locationId) {
        map[locationId] = {
          directorate: location.directorate,
          department: location.department,
          wardDepartmentArea: location.wardDepartmentArea,
          costCentreCode: location.costCentreCode,
        };
      }
    });
    return map;
  }, [hospitalLocations]);

  // Enrich pre-shifts with location data. This IS the manager's in-scope
  // list — no further ward/directorate filtering happens client-side
  // anymore, since the backend already restricted it.
  const managerPreShifts = useMemo(() => {
    if (!preShifts || !hospitalLocations) return [];

    return preShifts.map(preShift => {
      const locationId = extractMongoId(preShift.hospitalLocationId);
      const locationData = locationId ? hospitalLocationMap[locationId] : null;

      return {
        ...preShift,
        directorate: locationData?.directorate || preShift.directorate || 'N/A',
        department: locationData?.department || 'N/A',
        displayWardDepartmentArea: preShift.wardDepartmentArea || locationData?.wardDepartmentArea || 'N/A',
        requestedDate: new Date(preShift.createdAt).toLocaleDateString(),
        shiftDate: new Date(preShift.startDateTime).toLocaleDateString(),
        shiftTime: `${new Date(preShift.startDateTime).toLocaleTimeString()} - ${new Date(preShift.endDateTime).toLocaleTimeString()}`,
      };
    });
  }, [preShifts, hospitalLocations, hospitalLocationMap]);

  // Extract unique filter options from the (already scoped) list — built
  // from the actual data returned rather than assumed to be a single
  // directorate, since an onlyOwnWard manager or one with inclusions from
  // another directorate can have wards spanning more than one directorate.
  const filterOptions = useMemo(() => {
    if (!managerPreShifts || managerPreShifts.length === 0) {
      return { wards: [], categories: [], statuses: [] };
    }

    return {
      wards: [...new Set(
        managerPreShifts
          .map(shift => shift.displayWardDepartmentArea)
          .filter(ward => ward && ward !== 'N/A')
      )].sort(),
      categories: [...new Set(
        managerPreShifts
          .map(shift => shift.categoryOfStaff)
          .filter(cat => cat)
      )].sort(),
      statuses: [...new Set(
        managerPreShifts
          .map(shift => shift.preShiftStatus)
          .filter(status => status)
      )].sort(),
    };
  }, [managerPreShifts]);

  // Filter pre-shifts (local narrowing on top of the already-scoped list)
  const filteredPreShifts = useMemo(() => {
    if (!managerPreShifts) return [];

    return managerPreShifts.filter(preShift => {
      if (filters.wardDepartmentArea && preShift.displayWardDepartmentArea !== filters.wardDepartmentArea) return false;
      if (filters.categoryOfStaff && preShift.categoryOfStaff !== filters.categoryOfStaff) return false;
      if (filters.preShiftStatus && preShift.preShiftStatus !== filters.preShiftStatus) return false;

      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        const shiftStartDate = new Date(preShift.startDateTime);
        if (shiftStartDate < startDate) return false;
      }

      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        const shiftEndDate = new Date(preShift.endDateTime);
        if (shiftEndDate > endDate) return false;
      }

      return true;
    });
  }, [managerPreShifts, filters]);


  // Pending items in current filtered view
  const filteredPendingPreShifts = useMemo(
    () => filteredPreShifts.filter(ps => ps.preShiftStatus === 'Pending'),
    [filteredPreShifts]
  );

  // Status breakdown: count + % for each status in the filtered list
  const statusBreakdown = useMemo(() => {
    const total = filteredPreShifts.length;
    if (total === 0) return [];

    const counts = filteredPreShifts.reduce((acc, ps) => {
      const s = ps.preShiftStatus || 'Unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    const ORDER = ['Pending', 'Approved', 'Rejected'];
    const COLOR = {
      Pending:  { bg: orange[400],     text: '#fff' },
      Approved: { bg: lightGreen[500], text: '#fff' },
      Rejected: { bg: red[400],        text: '#fff' },
      Unknown:  { bg: grey[400],       text: '#fff' },
    };

    const keys = [
      ...ORDER.filter(s => counts[s]),
      ...Object.keys(counts).filter(s => !ORDER.includes(s)),
    ];

    return keys.map(status => ({
      status,
      count: counts[status],
      pct: Math.round((counts[status] / total) * 100),
      ...(COLOR[status] || COLOR.Unknown),
    }));
  }, [filteredPreShifts]);

  const isFilterActive = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'dateRange') return value.start || value.end;
      return Boolean(value);
    });
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      wardDepartmentArea: '',
      categoryOfStaff: '',
      preShiftStatus: '',
      dateRange: { start: '', end: '' },
    });
  };

  const handleFilterChange = (filterName, value) => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    if (filterName.includes('dateRange')) {
      const [range, type] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [type]: value,
        },
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value,
      }));
    }
  };

  // Column definitions
  const columns = [
    {
      field: 'requestedDate',
      headerName: 'Requested',
      width: 100,
    },
    {
      field: 'shiftDate',
      headerName: 'Shift Date',
      width: 100,
    },
    {
      field: 'shiftTime',
      headerName: 'Shift Time',
      width: 180,
    },
    {
      field: 'displayWardDepartmentArea',
      headerName: 'Ward/Dept Area',
      width: 150,
    },
    {
      field: 'directorate',
      headerName: 'Directorate',
      width: 150,
    },
    {
      field: 'categoryOfStaff',
      headerName: 'Staff Category',
      width: 120
    },
    {
      field: 'reasonForRequest',
      headerName: 'Reason',
      width: 120
    },
    {
      field: 'preShiftStatus',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const status = params.value;
        const meta = getStatusMeta(status);
        return (
          <Chip
            label={status}
            color={meta.chipColor}
            size="small"
            icon={meta.icon}
          />
        );
      }
    },
    {
      field: 'requestedByName',
      headerName: 'Requested By',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>

          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPreShift(params.row);
                setOpenViewModal(true);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {params.row.preShiftStatus === 'Pending' && (
            <>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenDeleteModal(true);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {params.row.preShiftStatus === 'Approved' && params.row.createdShiftId && (
            <Tooltip title="View Created Shift">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/manager/shifts/${params.row.createdShiftId}/edit`);
                }}
              >
                View Shift
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Row styling function (desktop grid)
  const getRowClassName = (params) => getStatusMeta(params.row.preShiftStatus).rowClass;

  // Handle approve
  const handleApprove = async () => {
    try {
      await approvePreShift({
        id: selectedPreShift._id,
        approvalNotes,
      }).unwrap();

      toast.success('Pre-shift approved and shift created successfully!');
      setOpenApproveModal(false);
      setSelectedPreShift(null);
      setApprovalNotes('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to approve pre-shift');
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await rejectPreShift({
        id: selectedPreShift._id,
        rejectionReason,
      }).unwrap();

      toast.success('Pre-shift rejected successfully!');
      setOpenRejectModal(false);
      setSelectedPreShift(null);
      setRejectionReason('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reject pre-shift');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePreShift(selectedPreShift._id).unwrap();
      toast.success('Pre-shift deleted successfully!');
      setOpenDeleteModal(false);
      setSelectedPreShift(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete pre-shift');
    }
  };

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: isMobile ? 'auto' : 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/manager/ward-preshifts/create')}
          fullWidth={isMobile}
        >
          Create Pre-Shift Request
        </Button>

        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
          <Tooltip title="Toggle Filters">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              fullWidth={isMobile}
            >
              Filters {isFilterActive && (
                <Chip
                  size="small"
                  label="Active"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ward/Dept Area</InputLabel>
                <Select
                  value={filters.wardDepartmentArea}
                  label="Ward/Dept Area"
                  onChange={(e) => handleFilterChange('wardDepartmentArea', e.target.value)}
                >
                  <MenuItem value="">All Areas</MenuItem>
                  {filterOptions.wards.map(ward => (
                    <MenuItem key={ward} value={ward}>{ward}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Category</InputLabel>
                <Select
                  value={filters.categoryOfStaff}
                  label="Staff Category"
                  onChange={(e) => handleFilterChange('categoryOfStaff', e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {filterOptions.categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.preShiftStatus}
                  label="Status"
                  onChange={(e) => handleFilterChange('preShiftStatus', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {filterOptions.statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!isFilterActive}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Scope Info Banner */}
      <Paper sx={{ p: 1, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="body2">
          Showing pre-shift requests for: <strong>{scopeLabel}</strong>
        </Typography>
      </Paper>

      {/* Results Summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle1" color="textSecondary">
          Showing {filteredPreShifts.length} of {managerPreShifts?.length || 0} pre-shift requests for your scope
          {filteredPendingPreShifts.length > 0 && (
            <Chip
              size="small"
              label={`${filteredPendingPreShifts.length} pending`}
              color="warning"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        {isFilterActive && (
          <Chip
            label={`${filteredPreShifts.length} filtered`}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Status breakdown strip */}
      {filteredPreShifts.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          {/* Segmented progress bar */}
          <Box sx={{ display: 'flex', height: 10 }}>
            {statusBreakdown.map(({ status, pct, bg }) => (
              <Tooltip key={status} title={`${status}: ${pct}%`} arrow>
                <Box
                  sx={{
                    width: `${pct}%`,
                    bgcolor: bg,
                    transition: 'width 0.4s ease',
                    '&:first-of-type': { borderRadius: '8px 0 0 0' },
                    '&:last-of-type':  { borderRadius: '0 8px 0 0' },
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Stat cards row */}
          <Box sx={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            {statusBreakdown.map(({ status, count, pct, bg }, idx) => (
              <Box
                key={status}
                sx={{
                  flex: isMobile ? '1 1 50%' : 1,
                  px: 2,
                  py: 1,
                  borderRight: !isMobile && idx < statusBreakdown.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: bg, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {status}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({pct}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Total */}
            <Box sx={{
              flex: isMobile ? '1 1 100%' : '0 0 auto',
              px: 2, py: 1,
              borderLeft: !isMobile ? '1px solid' : 'none',
              borderTop: isMobile ? '1px solid' : 'none',
              borderColor: 'divider',
              display: 'flex', alignItems: 'center', bgcolor: 'grey.50',
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>Total</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{filteredPreShifts.length}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Main Content */}
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader
          title="Pre-Shift Requests"
          subheader={`Review and approve shift requests for ${scopeLabel}`}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            borderBottom: '1px solid #ddd',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        />

        {isMobile ? (
          /* ── Mobile card list ── */
          <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Stack spacing={1.5}>
              {filteredPreShifts.map((preShift) => {
                const meta = getStatusMeta(preShift.preShiftStatus);
                const rowId = extractMongoId(preShift._id) || preShift._id;
                return (
                  <Card
                    key={rowId}
                    elevation={2}
                    sx={{ borderLeft: `6px solid ${meta.accent}` }}
                  >
                    <CardContent
                      sx={{ pb: 1, cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedPreShift(preShift);
                        setOpenViewModal(true);
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {preShift.displayWardDepartmentArea}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {preShift.directorate}
                          </Typography>
                        </Box>
                        <Chip label={preShift.preShiftStatus} color={meta.chipColor} size="small" icon={meta.icon} />
                      </Box>

                      <Typography variant="body2" color="textSecondary">
                        <strong>Shift:</strong> {preShift.shiftDate} · {preShift.shiftTime}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Requested:</strong> {preShift.requestedDate} by {preShift.requestedByName || 'Unknown'}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="body2">
                        <strong>Staff Category:</strong> {preShift.categoryOfStaff || '—'}
                      </Typography>
                      {preShift.reasonForRequest && (
                        <Typography variant="body2">
                          <strong>Reason:</strong> {preShift.reasonForRequest}
                        </Typography>
                      )}
                    </CardContent>

                    {/* Actions mirror the desktop grid exactly: View always; Delete
                        for Pending items (Approve/Reject are disabled for managers,
                        same as the commented-out buttons in the grid's actions
                        column); View Shift for Approved items with a created shift. */}
                    <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPreShift(preShift);
                          setOpenViewModal(true);
                        }}
                      >
                        View
                      </Button>

                      {preShift.preShiftStatus === 'Pending' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPreShift(preShift);
                            setOpenDeleteModal(true);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}

                      {preShift.preShiftStatus === 'Approved' && preShift.createdShiftId && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/manager/shifts/${preShift.createdShiftId}/edit`);
                          }}
                        >
                          View Shift
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })}
            </Stack>

            {filteredPreShifts.length === 0 && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 200,
                color: 'text.secondary',
                flexDirection: 'column',
                gap: 1,
              }}>
                <Typography variant="h6">No pre-shift requests found</Typography>
                <Typography variant="body2" align="center">
                  {isFilterActive
                    ? "No requests match your current filters. Try adjusting or clearing filters."
                    : `No pre-shift requests available for ${scopeLabel}. Create a new request to get started.`}
                </Typography>
                {isFilterActive && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear All Filters
                  </Button>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {/* Fixed Header Row */}
            <Box sx={fixedHeaderStyle}>
              <Grid container sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
                {columns.map((column) => (
                  <Grid item key={column.field} sx={{
                    width: column.width,
                    minWidth: column.width,
                    p: 1,
                    borderRight: 1,
                    borderColor: 'divider',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {column.headerName}
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Data Grid with pagination */}
            <Box sx={{ height: 'calc(100vh - 420px)', width: '100%' }}>
              <StyledDataGrid
                density="compact"
                rows={filteredPreShifts}
                columns={columns}
                getRowId={(row) => extractMongoId(row._id) || row._id}
                getRowClassName={getRowClassName}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                disableColumnMenu
                disableDensitySelector
                onRowClick={(params) => {
                  setSelectedPreShift(params.row);
                  setOpenViewModal(true);
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: 'grey.50',
                  },
                }}
              />

              {filteredPreShifts.length === 0 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200,
                  color: 'text.secondary',
                  flexDirection: 'column',
                  gap: 1,
                }}>
                  <Typography variant="h6">No pre-shift requests found</Typography>
                  <Typography variant="body2">
                    {isFilterActive
                      ? "No requests match your current filters. Try adjusting or clearing filters."
                      : `No pre-shift requests available for ${scopeLabel}. Create a new request to get started.`}
                  </Typography>
                  {isFilterActive && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}

        <Box sx={{
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="caption" color="textSecondary">
            Total: {filteredPreShifts.length} requests for {scopeLabel}
          </Typography>
          {!isMobile && (
            <Typography variant="caption" color="textSecondary">
              Click any row to view details
            </Typography>
          )}
        </Box>
      </Card>

      {/* View Details Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Pre-Shift Request Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenViewModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Request Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip
                      label={selectedPreShift.preShiftStatus}
                      color={selectedPreShift.preShiftStatus === 'Pending' ? 'warning' :
                             selectedPreShift.preShiftStatus === 'Approved' ? 'success' : 'error'}
                      icon={selectedPreShift.preShiftStatus === 'Pending' ? <HistoryIcon /> :
                            selectedPreShift.preShiftStatus === 'Approved' ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                    <Typography variant="body2">
                      Requested on: {new Date(selectedPreShift.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Shift Details</Typography>
                <Typography><strong>Start:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()}</Typography>
                <Typography><strong>End:</strong> {new Date(selectedPreShift.endDateTime).toLocaleString()}</Typography>
                <Typography><strong>Category:</strong> {selectedPreShift.categoryOfStaff}</Typography>
                <Typography><strong>Reason:</strong> {selectedPreShift.reasonForRequest}</Typography>
                    {selectedPreShift.specialsReason && (
                      <Typography>
                        <strong>Specials / Cohorts Reason:</strong> {selectedPreShift.specialsReason}
                      </Typography>
                    )}
                    <Typography><strong>Details:</strong> {selectedPreShift.reasonForRequestDetails}</Typography>
                {selectedPreShift.specialRequirements && (
                  <Typography><strong>Special Req:</strong> {selectedPreShift.specialRequirements}</Typography>
                )}
                {selectedPreShift.comments && (
                  <Typography><strong>Comments:</strong> {selectedPreShift.comments}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Location Details</Typography>
                <Typography><strong>Directorate:</strong> {selectedPreShift.directorate}</Typography>
                <Typography><strong>Ward/Dept:</strong> {selectedPreShift.displayWardDepartmentArea}</Typography>
                <Typography><strong>Department:</strong> {selectedPreShift.department}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Requestor Details</Typography>
                <Typography><strong>Name:</strong> {selectedPreShift.requestedByName}</Typography>
                <Typography><strong>Email:</strong> {selectedPreShift.requestedByEmail}</Typography>
              </Grid>

              {selectedPreShift.preShiftStatus !== 'Pending' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="primary">Approval Details</Typography>
                  <Typography><strong>Approved By:</strong> {selectedPreShift.approvedByName || 'N/A'}</Typography>
                  <Typography><strong>Approved At:</strong> {selectedPreShift.approvedAt ? new Date(selectedPreShift.approvedAt).toLocaleString() : 'N/A'}</Typography>
                  {selectedPreShift.rejectionReason && (
                    <Typography><strong>Rejection Reason:</strong> {selectedPreShift.rejectionReason}</Typography>
                  )}
                  {selectedPreShift.approvalNotes && (
                    <Typography><strong>Approval Notes:</strong> {selectedPreShift.approvalNotes}</Typography>
                  )}
                </Grid>
              )}

              {selectedPreShift.preShiftStatus === 'Approved' && selectedPreShift.createdShiftId && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <AlertTitle>Shift Created</AlertTitle>
                    This request has been approved and a shift has been created.
                    <Button
                      size="small"
                      sx={{ ml: 2 }}
                      variant="outlined"
                      onClick={() => {
                        setOpenViewModal(false);
                        navigate(`/manager/shifts/${selectedPreShift.createdShiftId}/edit`);
                      }}
                    >
                      View Created Shift
                    </Button>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>

          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          {selectedPreShift?.preShiftStatus === 'Pending' && (
            <>
            {/*
              Approve/Reject are admin-only actions (see preShiftRoutes.js —
              /:id/approve and /:id/reject both require the `admin`
              middleware), so they stay disabled here even though this
              screen is otherwise manager-facing. A manager's only action on
              a pending request is to delete it.
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenApproveModal(true);
                }}
              >
                Approve
              </Button>


              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenRejectModal(true);
                }}
              >
                Reject
              </Button>

              */}

            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={openApproveModal} onClose={() => setOpenApproveModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Approve Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenApproveModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Approval</AlertTitle>
                This action will create an actual shift from this request.
              </Alert>

              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Staff Category:</strong> {selectedPreShift.categoryOfStaff}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Approval Notes (Optional)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenApproveModal(false);
            setApprovalNotes('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={openRejectModal} onClose={() => setOpenRejectModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenRejectModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Rejection</AlertTitle>
                This request will be marked as rejected and no shift will be created.
              </Alert>

              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Rejection Reason *"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                error={!rejectionReason.trim()}
                helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenRejectModal(false);
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting || !rejectionReason.trim()}
          >
            {rejecting ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delete Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error">
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to delete this pre-shift request? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WardPreShiftListScreen;